import { Col, Row, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CategoryContext from "../../../context/category/context";
import { ICreateCategory } from "../../../models/category/request";
import { languages } from "../../../utils/helpers/constants";
import { getTranslations } from "../../../utils/helpers/translations";
import Controller from "../../form-components/controller";
import FieldBuilder from "../../form-components/field-builder";
import GeneralForm from "../../form-components/general-form";
import Spin from "../../general/antd/spin";
import FormItem from "../../general/form-item";
import { MediumFor } from "../../../models/medium/enum";
import EndPoints from "../../../services/end-points";
import { IMedium } from "../../../models/medium/response";
import ImageFieldBlock from "../../general/image-field-block";
import { errorNotification, successNotification } from "../../../utils/helpers/notification";

const Form = () => {
  const { t } = useTranslation();
  const { details, actions, loading, allCategories } =
    useContext(CategoryContext);
  const { id } = useParams();
  const { control, setValue, trigger, getValues, reset } = useForm();

  const [newImages, setNewImages] = useState<Record<string, string | null>>({});
  const [imageChanged, setImageChanged] = useState<Record<string, boolean>>({});
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
  const [withoutBackgrounds, setWithoutBackgrounds] = useState<Record<string, boolean>>({});

  const handleImageChange = (fieldName: string) => (response: IMedium | null) => {
    setNewImages((prev) => ({ ...prev, [fieldName]: response?.url ?? null }));
    setImageChanged((prev) => ({ ...prev, [fieldName]: true }));
  };

  const handleUploadNewImage = async (fieldName: string) => {
    const newImageUrl = newImages[fieldName];
    if (!newImageUrl) {
      errorNotification(`No new image URL found for field: ${fieldName}`);
      return;
    }

    try {
      setImageLoading(prev => ({ ...prev, [fieldName]: true }));

      const res = await fetch(newImageUrl);

      if (!res.ok) {
        throw new Error(`Failed to fetch image: ${res.status} ${res.statusText}`);
      }

      const blob = await res.blob();
      const timestamp = new Date().getTime();
      const file = new File([blob], `${fieldName}-${timestamp}.png`, {
        type: blob.type || "image/png"
      });

      const formData = new FormData();
      formData.append("medium", file);
      formData.append("type", "1");
      formData.append("for", MediumFor.UserProfile);

      const withoutBgValue = withoutBackgrounds[fieldName] ?? false;
      formData.append("without_background", String(withoutBgValue));

      const uploadRes = await EndPoints.media.uploadMedia(formData);
      const uploadedId = uploadRes?.id;

      if (!uploadedId) {
        throw new Error(`Invalid upload response for ${fieldName} - no ID returned`);
      }

      setValue(fieldName, uploadedId, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });

      await trigger(fieldName);

      setImageChanged((prev) => ({ ...prev, [fieldName]: false }));

      successNotification(t(`image_uploaded_successfully`));

    } catch (err) {
      console.error(`Upload failed for ${fieldName}:`, err);

      let errorMessage = t("failed_to_upload_image");

      if (err.message.includes("Failed to fetch")) {
        errorMessage = t("failed_to_load_image");
      } else if (err.message.includes("Invalid upload response")) {
        errorMessage = t("server_error_try_again");
      }

      errorNotification(`${errorMessage} (${fieldName})`);
    } finally {
      setImageLoading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleUpdateBackground = async (fieldName: string, imageId: number) => {
    try {
      await EndPoints.media.updateMedia(imageId, Boolean(withoutBackgrounds[fieldName]));
      successNotification("تم تحديث نوع الخلفية بنجاح");
    } catch (err) {
      errorNotification("فشل التحديث");
    }
  };

  const handleDeleteImage = async (fieldName: string) => {
    try {
      setImageLoading(prev => ({ ...prev, [fieldName]: true }));
      setValue(fieldName, null);
    } finally {
      setImageLoading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  useEffect(() => {
    if (id) {
      actions.getDetails(Number(id));
    } else {
      actions.setDetails(null);
    }
  }, [id]);

  const grandCategory = useWatch({
    control,
    name: "grandId",
  });
  const parentCategory = useWatch({
    control,
    name: "parentId",
  });

  // useEffect(() => {
  //   setValue("grandId", details?.grand?.id ?? undefined, {
  //     shouldDirty: true,
  //     shouldTouch: true,
  //     shouldValidate: true
  //   });
  //   setValue("parentId", details?.parent?.id ?? undefined, {
  //     shouldDirty: true,
  //     shouldTouch: true,
  //     shouldValidate: true
  //   });

  //   if (details) {
  //     setValue("imageId", details?.image?.id, { shouldDirty: false });
  //     setValue("mainImageId", details?.mainImage?.id, { shouldDirty: false });

  //   }
  //   setWithoutBackgrounds(({ mainImageId: details?.mainImage?.without_background, imageId: details?.image?.without_background }))
  //   actions.getAllData();
  // }, [details]);
  useEffect(() => {
    !allCategories && actions.getAllData();
    if (details) {
      reset({
        name: getTranslations(details?.translations?.name),
        note: getTranslations(details?.translations?.note),
        order: details?.order,
        parentId: details?.parent?.id ?? undefined,
        grandId: details?.grand?.id ?? undefined,
        percent: details?.percent ?? undefined,
        imageId: details?.image?.id,
        mainImageId: details?.mainImage?.id,
        priority: details?.priority ?? undefined,
        acceptedExchange: details?.acceptedExchange ?? false,
      });
      setWithoutBackgrounds(({ mainImageId: details?.mainImage?.without_background, imageId: details?.image?.without_background }))
    }
  }, [details]);


  const watchImageId = useWatch({ control, name: "imageId" });
  const watchMainImageId = useWatch({ control, name: "mainImageId" });

  useEffect(() => {
    if (details?.image?.id !== watchImageId)
      setImageChanged(prev => ({ ...prev, imageId: true }));
  }, [watchImageId]);

  useEffect(() => {
    if (details?.mainImage?.id !== watchMainImageId)
      setImageChanged(prev => ({ ...prev, mainImageId: true }));
  }, [watchMainImageId]);

  useEffect(() => {
    const values = getValues()
    console.log(values)
  }, [details])
  return (
    <GeneralForm
      values={[
        {
          key: "grandId",
          value: grandCategory ?? undefined,
        },
        {
          key: "parentId",
          value: parentCategory ?? undefined,
        },
        {
          key: "imageId",
          value: watchImageId ?? undefined,
        },
        {
          key: "mainImageId",
          value: watchMainImageId ?? undefined,
        },
      ]}
      onSubmit={async (data) => {
        details
          ? await actions.updateCategory(details?.id, data)
          : await actions.createCategory(data);
        actions.setDetails(null);
      }}
      title={details ? t("update_category") : t("add_category")}
      defaultValues={
        {
          name: getTranslations(details?.translations?.name),
          note: getTranslations(details?.translations?.note) ?? null,
          order: details?.order,
          parentId: details?.parent?.id ?? undefined,
          percent: details?.percent ?? undefined,
          grandId: details?.grand?.id ?? undefined,
          imageId: details?.image?.id,
          mainImageId: details?.mainImage?.id,
          priority: details?.priority ?? undefined,
          acceptedExchange: details?.acceptedExchange ?? false

        } as ICreateCategory
      }
    >
      {loading.includes("details") ? (
        <div style={{ height: "50vh" }}>
          <Spin />
        </div>
      ) : (
        <>
          <Row gutter={[16, 8]}>
            {languages?.map((lang, index) => {
              return (
                <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                  <FieldBuilder
                    key={index}
                    name={`name[${lang?.code}]`}
                    label={`${t("name")} (${lang?.name})`}
                    input={{ type: "text" }}
                    rules={{ required: true }}
                  />
                </Col>
              );
            })}

            <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
              <FormItem label={t("grand")}>
                <Controller
                  name="grandId"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { ...field } }) => {
                    return (
                      <Select
                        loading={false}
                        {...field}
                        value={grandCategory}
                        style={{ width: "100%" }}
                        optionLabelProp="label"
                        optionFilterProp="label"
                        showSearch
                        allowClear
                        placeholder={t("grand")}
                        options={allCategories
                          ?.filter(
                            (cat) => cat?.parent === null && cat?.grand === null
                          )
                          .filter((cat) => cat?.id !== Number(id))
                          ?.map((tr, index) => {
                            return {
                              label: tr?.name,
                              value: tr?.id,
                            };
                          })}
                      />
                    );
                  }}
                />
              </FormItem>
            </Col>

            <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
              <FormItem label={t("parent")}>
                <Controller
                  name="parentId"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={parentCategory}
                      allowClear
                      showSearch
                      style={{ width: "100%" }}
                      placeholder={t("parent")}
                      options={allCategories
                        ?.filter((cat) => cat?.grand?.id === grandCategory)
                        .filter((cat) => cat?.id !== Number(id))
                        .filter((cat) => !cat?.parent)
                        .map((cat) => ({
                          label: cat.name,
                          value: cat.id,
                        }))}
                    />
                  )}
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="order"
                label={`${t("order")}`}
                input={{ type: "number" }}
                rules={{ required: true }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="priority"
                label={`${t("priority")}`}
                input={{ type: "number" }}
                rules={{ required: true }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="percent"
                label={`${t("percent")}`}
                input={{ type: "number" }}
                rules={{
                  required: false,
                  min: 0,
                  max: 100
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6}>
              <FieldBuilder
                label={`${t("accepted_exchange")}`}
                name="acceptedExchange"
                input={{ type: "checkBox" }}
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
            </Col>
            {languages?.map((lang, index) => {
              return (
                <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
                  <FieldBuilder
                    key={index}
                    name={`note[${lang?.code}]`}
                    label={`${t("note")} (${lang?.name})`}
                    input={{ type: "text-area" }}
                    rules={{ required: false }}
                  />
                </Col>
              );
            })}

            <ImageFieldBlock
              fieldName="imageId"
              currentId={details?.image?.id}
              fieldValue={withoutBackgrounds["imageId"]}
              currentImage={details?.image}
              newImageUrl={newImages["imageId"] ?? null}
              isChanged={imageChanged["imageId"]}
              onUpload={() => handleUploadNewImage("imageId")}
              onUpdateBackground={() => handleUpdateBackground("imageId", details?.image?.id)}
              onImageChange={handleImageChange("imageId")}
              setWithoutBackgrounds={(val) =>
                setWithoutBackgrounds((prev) => ({ ...prev, imageId: val }))
              }
              onDeleteImage={() => handleDeleteImage("imageId")}
              isImageLoading={imageLoading["imageId"]}
              value={withoutBackgrounds["imageId"]}
              force={true}
            />

            <ImageFieldBlock
              title='main_image'
              fieldName="mainImageId"
              notRequired={true}
              currentId={details?.mainImage?.id}
              fieldValue={withoutBackgrounds["mainImageId"]}
              currentImage={details?.mainImage}
              newImageUrl={newImages["mainImageId"] ?? null}
              isChanged={imageChanged["mainImageId"]}
              onUpload={() => handleUploadNewImage("mainImageId")}
              onUpdateBackground={() => handleUpdateBackground("mainImageId", details?.mainImage?.id)}
              onImageChange={handleImageChange("mainImageId")}
              setWithoutBackgrounds={(val) =>
                setWithoutBackgrounds((prev) => ({ ...prev, mainImageId: val }))
              }
              onDeleteImage={() => handleDeleteImage("mainImageId")}
              isImageLoading={imageLoading["mainImageId"]}
              value={withoutBackgrounds["mainImageId"]}
              force={true}
            />

          </Row>
        </>
      )}
    </GeneralForm>
  );
};

const CategoryForm = () => {
  return <Form />;
};
export default CategoryForm;
